import { KeyboardArrowRight } from "@mui/icons-material";
import { ChevronDown, ChevronUp } from "lucide-react";
import React, { useState, useEffect, useMemo } from "react";
import { Divider, Switch, Alert } from "@mui/material";
import AudioPlayerForRouting from "../reusables/AudioPlayerForRouting";
import TooltipComponent from "../reusables/TooltipComponent";
import RouteSelect from "../reusables/RouteSelect";
import { formatPhoneNumber } from "../utils/formatnumber";
import { DYNAMIC_FORWARD_BACKUP_NONE } from "../../hooks/useCallAction";
import { isDynamicForwardAvailable } from "../../config/callActionOptions";
import { useRouteConfigModals } from "../contexts/RouteConfigModalsContext";
import { useDynamicForwardUpgrade } from "../../hooks/useDynamicForwardUpgrade";
import DynamicForwardUpgradeModal from "../modals/DynamicForwardUpgradeModal";

const DEFAULT_CALL_APPROVAL = "enabled";
const DEFAULT_CALLER_ID = "twilio";

/**
 * Pure UI component for Dynamic Forward action
 * All logic is handled by useDynamicForwardAction hook
 */
const DynamicForwardUI = ({
  numberValue,
  callerIdPref,
  callApproval,
  recordingValue,
  twilanswerVmFallback,
  handleNumberChange,
  handleCallerIdChange,
  handleCallApprovalChange,
  handleRecordingChange,
  handleTwilanswerVmFallbackChange,
  containerClass,
  headerSize,
  showDivider,
  callerIdOptions,
  setOpenConfigureSlotModal,
  channels = [],
  recordings = [],
  hasValidationError = false,
  addDestinationOption,
  addRecordingOption,
  onDestinationChange,
}) => {
  const planDetails = useRouteConfigModals()?.planDetails;
  const isPlanLocked = planDetails != null && !isDynamicForwardAvailable(planDetails);
  const { requestUpgrade, upgradeOpen, goToBilling, cancelUpgrade } = useDynamicForwardUpgrade();
  const defaultCallerId = DEFAULT_CALLER_ID;
  const isNonDefaultCallerId = callerIdPref !== defaultCallerId && callerIdPref !== "";
  const hasNonDefaultAdvanced = isNonDefaultCallerId || callApproval !== DEFAULT_CALL_APPROVAL;
  const [advancedOpen, setAdvancedOpen] = useState(() => hasNonDefaultAdvanced);

  const backupIsNone = !numberValue || numberValue === DYNAMIC_FORWARD_BACKUP_NONE;
  // Hide recording when a backup number is set and Twilanswer VM fallback is off
  const showRecording = backupIsNone || !!twilanswerVmFallback;

  useEffect(() => {
    if (hasNonDefaultAdvanced) {
      setAdvancedOpen(true);
    }
  }, [hasNonDefaultAdvanced]);

  const destinationOptions = useMemo(() => {
    const forwardOptions = (channels || [])
      .filter((item) => item.M.type.S === "forward_number")
      .map((item) => {
        const chId = item.M.ch_id?.S ?? item.ch_id ?? item.M.value?.S;
        const formattedNumber = formatPhoneNumber(item.M.value?.S ?? item.M.value ?? "");
        const label = item.M.label?.S ?? item.label ?? "";
        return { value: chId, label: label ? `${label} - ${formattedNumber}` : formattedNumber };
      });
    return [
      { value: DYNAMIC_FORWARD_BACKUP_NONE, label: "None — send to voicemail" },
      ...forwardOptions,
    ];
  }, [channels]);

  const destinationValue = useMemo(() => {
    if (backupIsNone) return DYNAMIC_FORWARD_BACKUP_NONE;
    const hasMatch = destinationOptions.some((o) => o.value === numberValue);
    if (hasMatch) return numberValue;
    const byPhone = channels?.find(
      (c) => c.M?.type?.S === "forward_number" && (c.M.value?.S ?? c.value) === numberValue
    );
    if (byPhone) return byPhone.M?.ch_id?.S ?? byPhone.ch_id ?? numberValue;
    return numberValue;
  }, [numberValue, destinationOptions, channels, backupIsNone]);

  const recordingOptions = useMemo(() => {
    if (!recordings) return [];
    return recordings.map((item) => ({ value: item.M.rec_id.S, label: item.M.title.S }));
  }, [recordings]);

  const recordingUrl = recordingValue
    ? recordings?.find((e) => e.M.rec_id.S === recordingValue)?.M.url.S
    : null;

  const handleDestinationSelect = (selected) => {
    if (selected === DYNAMIC_FORWARD_BACKUP_NONE) {
      (onDestinationChange ?? handleNumberChange)(DYNAMIC_FORWARD_BACKUP_NONE);
      return;
    }
    (onDestinationChange ?? handleNumberChange)(selected);
  };

  return (
    <div>
      <div className={`${containerClass}${hasValidationError ? " !bg-red-50/80" : ""}`}>
        <div className="flex flex-nowrap items-center justify-between gap-3 mb-3">
          <div className="flex items-center min-w-0 flex-1">
            <KeyboardArrowRight className="text-green-500 shrink-0" />
            <div className="min-w-0 ml-2">
              <h2 className={`${headerSize} text-gray-800`}>
                Dynamic Forward (beta)
              </h2>
              <div className="text-[13px]">
                Forward to the contact&apos;s assigned user
              </div>
            </div>
          </div>
          {setOpenConfigureSlotModal && (
            <button
              className="shrink-0 whitespace-nowrap text-sm text-blue-600 font-medium hover:underline text-[13px]"
              onClick={() => setOpenConfigureSlotModal(true)}
            >
              ↻ Change
            </button>
          )}
        </div>
        {showDivider && <Divider />}

        {isPlanLocked && (
          <Alert color="warning" sx={{ mb: 3 }}>
            <div
              className="text-[14px] hover:underline hover:text-blue-600 cursor-pointer"
              onClick={requestUpgrade}
            >
              Upgrade to the Team Plan to use Dynamic Forward
            </div>
          </Alert>
        )}

        <div className={`${isPlanLocked ? "opacity-60 pointer-events-none" : ""}`}>
        <div className="mb-4">
          <div className="flex items-center gap-1 mb-1">
            <span className="text-sm font-medium text-gray-700">
              Backup Forwarding Destination
            </span>
            <TooltipComponent message={"Used when there is no matching contact or the contact has no assignee"} />
          </div>
          <RouteSelect
            value={destinationValue}
            onChange={handleDestinationSelect}
            options={destinationOptions}
            placeholder="Select a backup destination"
            addOption={addDestinationOption}
          />
        </div>

        <div className="mb-4 flex items-center justify-between gap-4">
          <div className="min-w-0">
            <div className="flex items-center gap-1">
              <span className="text-sm font-medium text-gray-700">
                Twilanswer Voicemail Fallback
              </span>
              <TooltipComponent message={"When enabled, unanswered calls play a Twilanswer voicemail instead of the user's voicemail"} />
            </div>
            <p className="text-[12px] text-gray-500 mt-1">
              Play Twilanswer voicemail when the assignee does not answer
            </p>
          </div>
          <Switch
            checked={!!twilanswerVmFallback}
            onChange={(e) => handleTwilanswerVmFallbackChange(e.target.checked)}
            color="primary"
          />
        </div>

        {showRecording && (
          <div className="mb-4">
            <div className="flex items-center gap-1 mb-1">
              <span className="text-sm font-medium text-gray-700">Recording</span>
              <TooltipComponent
                message={
                  backupIsNone
                    ? "Voicemail message to play when no contact or assignee is found"
                    : "Voicemail message to play when the assignee does not answer"
                }
              />
            </div>
            <RouteSelect
              value={recordingValue}
              onChange={handleRecordingChange}
              options={recordingOptions}
              placeholder="Select a recording"
              addOption={addRecordingOption}
            />
            {recordingUrl && (
              <div className="mt-3">
                <AudioPlayerForRouting src={recordingUrl} />
              </div>
            )}
          </div>
        )}

        <div>
          <button
            type="button"
            onClick={() => setAdvancedOpen((prev) => !prev)}
            className="flex items-center gap-1 text-sm text-gray-600 hover:text-gray-800 transition-colors"
          >
            {advancedOpen ? (
              <ChevronUp className="w-4 h-4" />
            ) : (
              <ChevronDown className="w-4 h-4" />
            )}
            <span>Advanced Settings</span>
          </button>
          {advancedOpen && (
            <div className="mt-3 space-y-4">
              <div>
                <div className="flex items-center gap-1 mb-1">
                  <span className="text-sm font-medium text-gray-700">
                    Caller ID Setting
                  </span>
                  <TooltipComponent message={"Choose which caller ID to see when the call is forwarded"} />
                </div>
                <RouteSelect
                  value={callerIdPref}
                  onChange={handleCallerIdChange}
                  options={callerIdOptions}
                  placeholder={showDivider ? "select preference" : "Select a preference"}
                />
              </div>
              <div>
                <div className="flex items-center gap-1 mb-1">
                  <span className="text-sm font-medium text-gray-700">
                    Call Approval
                  </span>
                  <TooltipComponent message={"When enabled, you'll hear who is calling and can press 1 to connect or let it go to voicemail"} />
                </div>
                <RouteSelect
                  value={callApproval}
                  onChange={handleCallApprovalChange}
                  options={[
                    { value: "disabled", label: "Disabled - Connect Immediately" },
                    { value: "enabled", label: "Enabled - Press 1 to connect" },
                  ]}
                  placeholder="Select"
                />
              </div>
            </div>
          )}
        </div>
        </div>
      </div>
      <DynamicForwardUpgradeModal
        open={upgradeOpen}
        onUpgrade={goToBilling}
        onCancel={cancelUpgrade}
      />
    </div>
  );
};

export default DynamicForwardUI;
